import { useEffect, useState } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Award, CalendarClock, MapPin, Target, Users } from "lucide-react";
import statisticsAPIService, {
	type GraduationYearStatistics,
	type PriorityAreaRow,
} from "@/lib/statistics-api.service";
import AgeDistributionTab from "./AgeDistributionTab";
import AwardStatisticsTab from "./AwardStatisticsTab";
import ProvinceDemographicsTab from "./ProvinceDemographicsTab";
import { PriorityAreaChart } from "./priority-area-chart";
import { GraduationYearChart } from "./graduation-year-chart";
import { StatisticsFilterControls, type StatisticsFilters } from "./StatisticsControls";
import { toAPIFilter } from "./utils";

interface OtherStatisticsTabProps {
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; chuyển tiếp xuống các mục con. */
	refreshToken?: number;
	/**
	 * Mục con đang mở. Do trang truyền xuống để nó nằm được trên URL — mở sẵn một
	 * mục cụ thể là thứ người ta muốn gửi cho nhau, không chỉ "tab Khác".
	 * Không truyền thì component tự giữ lấy, để dùng độc lập vẫn chạy.
	 */
	section?: string | null;
	onSectionChange?: (section: string | null) => void;
}

export default function OtherStatisticsTab({
	filters = {},
	onFiltersChange,
	refreshToken = 0,
	section,
	onSectionChange,
}: OtherStatisticsTabProps) {
	const [ownSection, setOwnSection] = useState<string | null>(null);
	const activeSection = onSectionChange ? (section ?? null) : ownSection;

	const handleSectionToggle = (next: string) => {
		const value = activeSection === next ? null : next;
		if (onSectionChange) onSectionChange(value);
		else setOwnSection(value);
	};

	// `undefined` = lần tải đầu chưa xong; chỉ fetch khi mục con đang mở, giống
	// ba mục con sẵn có (Tuổi/Giải thưởng/Địa lý) tự tải khi được chọn.
	const [priorityRows, setPriorityRows] = useState<PriorityAreaRow[] | undefined>(undefined);
	const [priorityError, setPriorityError] = useState<string | null>(null);
	const [graduationRows, setGraduationRows] = useState<GraduationYearStatistics[] | undefined>(
		undefined,
	);
	const [graduationError, setGraduationError] = useState<string | null>(null);

	useEffect(() => {
		if (activeSection !== "priority-area") return;
		let cancelled = false;
		statisticsAPIService
			.getPriorityAreas(toAPIFilter(filters, "total"))
			.then((data) => {
				if (cancelled) return;
				setPriorityRows(data);
				setPriorityError(null);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("Error fetching priority areas:", err);
				setPriorityError(err instanceof Error ? err.message : "Không tải được khu vực ưu tiên");
			});
		return () => {
			cancelled = true;
		};
	}, [activeSection, filters, refreshToken]);

	useEffect(() => {
		if (activeSection !== "graduation-year") return;
		let cancelled = false;
		statisticsAPIService
			.getStatsByGraduationYear(toAPIFilter(filters, "total"))
			.then((data) => {
				if (cancelled) return;
				setGraduationRows(data);
				setGraduationError(null);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("Error fetching graduation years:", err);
				setGraduationError(
					err instanceof Error ? err.message : "Không tải được năm tốt nghiệp THPT",
				);
			});
		return () => {
			cancelled = true;
		};
	}, [activeSection, filters, refreshToken]);

	return (
		<TabsContent value="other" className="space-y-6">
			<div>
				<h2 className="text-2xl font-bold text-foreground mb-2">Thống kê khác</h2>
				<p className="text-muted-foreground">Các thống kê nâng cao và chi tiết</p>
			</div>

			{onFiltersChange && (
				<StatisticsFilterControls
					filterType="total"
					onFilterChange={() => {}}
					chartLimit={10}
					onChartLimitChange={() => {}}
					showChartLimit={false}
					filters={filters}
					onFiltersChange={onFiltersChange}
					showFilters={true}
				/>
			)}

			{/* Statistics Triggers */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<Card
					className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
						activeSection === "age" ? "ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg" : ""
					}`}
					onClick={() => handleSectionToggle("age")}
				>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-lg">
							<Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
							Phân bố tuổi
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							Xem phân bố sinh viên theo độ tuổi và giới tính
						</p>
						<Button
							variant={activeSection === "age" ? "default" : "outline"}
							size="sm"
							className="mt-3 w-full"
						>
							{activeSection === "age" ? "Ẩn" : "Xem"} thống kê
						</Button>
					</CardContent>
				</Card>

				<Card
					className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
						activeSection === "awards" ? "ring-2 ring-green-500 dark:ring-green-400 shadow-lg" : ""
					}`}
					onClick={() => handleSectionToggle("awards")}
				>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-lg">
							<Award className="h-5 w-5 text-green-700 dark:text-green-400" />
							Giải thưởng
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							Thống kê sinh viên đạt giải thưởng và loại giải
						</p>
						<Button
							variant={activeSection === "awards" ? "default" : "outline"}
							size="sm"
							className="mt-3 w-full"
						>
							{activeSection === "awards" ? "Ẩn" : "Xem"} thống kê
						</Button>
					</CardContent>
				</Card>

				<Card
					className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
						activeSection === "geography"
							? "ring-2 ring-purple-500 dark:ring-purple-400 shadow-lg"
							: ""
					}`}
					onClick={() => handleSectionToggle("geography")}
				>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-lg">
							<MapPin className="h-5 w-5 text-purple-600 dark:text-purple-400" />
							Địa lý chi tiết
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							Phân tích chi tiết theo vùng miền và tỉnh thành
						</p>
						<Button
							variant={activeSection === "geography" ? "default" : "outline"}
							size="sm"
							className="mt-3 w-full"
						>
							{activeSection === "geography" ? "Ẩn" : "Xem"} thống kê
						</Button>
					</CardContent>
				</Card>

				<Card
					className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
						activeSection === "priority-area"
							? "ring-2 ring-amber-500 dark:ring-amber-400 shadow-lg"
							: ""
					}`}
					onClick={() => handleSectionToggle("priority-area")}
				>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-lg">
							<Target className="h-5 w-5 text-amber-600 dark:text-amber-400" />
							Khu vực ưu tiên
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							Thống kê sinh viên theo khu vực ưu tiên tuyển sinh
						</p>
						<Button
							variant={activeSection === "priority-area" ? "default" : "outline"}
							size="sm"
							className="mt-3 w-full"
						>
							{activeSection === "priority-area" ? "Ẩn" : "Xem"} thống kê
						</Button>
					</CardContent>
				</Card>

				<Card
					className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
						activeSection === "graduation-year"
							? "ring-2 ring-cyan-500 dark:ring-cyan-400 shadow-lg"
							: ""
					}`}
					onClick={() => handleSectionToggle("graduation-year")}
				>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-lg">
							<CalendarClock className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
							Năm tốt nghiệp THPT
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							Thống kê sinh viên theo năm tốt nghiệp THPT
						</p>
						<Button
							variant={activeSection === "graduation-year" ? "default" : "outline"}
							size="sm"
							className="mt-3 w-full"
						>
							{activeSection === "graduation-year" ? "Ẩn" : "Xem"} thống kê
						</Button>
					</CardContent>
				</Card>
			</div>

			{/* Conditional Sections */}
			{activeSection === "age" && (
				<div className="border-t pt-6">
					<AgeDistributionTab filters={filters} refreshToken={refreshToken} />
				</div>
			)}

			{activeSection === "awards" && (
				<div className="border-t pt-6">
					<AwardStatisticsTab filters={filters} refreshToken={refreshToken} />
				</div>
			)}

			{activeSection === "geography" && (
				<div className="border-t pt-6">
					<ProvinceDemographicsTab filters={filters} refreshToken={refreshToken} />
				</div>
			)}

			{activeSection === "priority-area" && (
				<div className="border-t pt-6">
					{priorityError && (
						<p className="mb-4 text-sm text-destructive" role="alert">
							{priorityError}
						</p>
					)}
					<PriorityAreaChart rows={priorityRows} />
				</div>
			)}

			{activeSection === "graduation-year" && (
				<div className="border-t pt-6">
					{graduationError && (
						<p className="mb-4 text-sm text-destructive" role="alert">
							{graduationError}
						</p>
					)}
					<GraduationYearChart rows={graduationRows} />
				</div>
			)}
		</TabsContent>
	);
}
